/**
 * exportCohortPDF — orchestrates the bulk PDF export for an entire cohort
 * of apprentices. Extracted from `EstablishmentsTab.jsx` (04/06/2026 refactor
 * pass) to keep the component focused on its UI concerns.
 *
 * Behaviour (unchanged from the original implementation) :
 *   1. Fetch the latest telemetry snapshot for every apprentice via the
 *      master overview endpoint.
 *   2. For each apprentice with a non-null snapshot, build their individual
 *      PDF in memory, then POST it to /api/admin/player-message so the
 *      player gets an in-game notification + an email with the PDF attached.
 *   3. Always build the supervisor's consolidated multi-page PDF.
 *   4. Surface a per-recipient success / failure tally via an alert.
 *
 * Returns a `{ sent, failed }` summary (mostly useful for tests).
 */

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    const idx = result.indexOf('base64,');
    resolve(idx >= 0 ? result.slice(idx + 7) : result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

/**
 * @param {Object} opts
 * @param {Object} opts.master             - Master license (Responsable Pédagogique)
 * @param {Array}  opts.children           - List of apprentice licenses
 * @param {string} opts.apiBase            - REACT_APP_BACKEND_URL value
 * @param {string} opts.token              - Bearer token for admin endpoints
 * @param {Function} [opts.alertFn=alert]  - Injectable for tests
 * @returns {Promise<{sent:number, failed:number, skipped:number}>}
 */
export default async function exportCohortPDF({
  master,
  children,
  apiBase,
  token,
  alertFn = (typeof window !== 'undefined' ? window.alert : () => {}),
} = {}) {
  if (!children || children.length === 0) {
    return { sent: 0, failed: 0, skipped: 0 };
  }

  let sent = 0;
  let failed = 0;
  let skipped = 0;

  const res = await fetch(`${apiBase}/api/admin/overview/all`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`overview/all failed: ${res.status}`);
  const data = await res.json();
  const byKey = new Map();
  (data.items || []).forEach(it => {
    if (it.license?.license_key) byKey.set(it.license.license_key, it);
  });
  const entries = children.map(c => {
    const found = byKey.get(c.license_key);
    return {
      license: c,
      snapshot: found?.snapshot || null,
    };
  });

  // Dynamic imports keep the modal lean — the PDF libs (~250 KB gzipped)
  // are only fetched when an export is actually triggered.
  const [{ default: generatePlayerProfilePDF }, { default: generateCohortProfilePDF }] = await Promise.all([
    import('./generatePlayerProfilePDF'),
    import('./generateCohortProfilePDF'),
  ]);

  // Serial loop — avoids hammering Resend in a tight burst.
  for (const entry of entries) {
    if (!entry.snapshot) { skipped += 1; continue; }
    try {
      const blob = generatePlayerProfilePDF({
        snapshot: entry.snapshot,
        license: entry.license,
        download: false,
      });
      const slug = (entry.license?.holder_name || entry.snapshot.company_name || 'apprenant')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
      const filename = `hst-fiche-${slug}.pdf`;
      const pdfBase64 = await blobToBase64(blob);
      const r = await fetch(`${apiBase}/api/admin/player-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          license_key: entry.license.license_key,
          subject: 'Fiche apprenant — votre profil de pilotage',
          body:
            "Votre responsable pédagogique vient de générer votre fiche apprenant. "
            + "Vous recevez ce message in-game ainsi qu'une copie par email avec le PDF en pièce jointe. "
            + "Prenez le temps de le consulter pour identifier vos points forts et vos axes d'amélioration.",
          sender_name: 'Responsable pédagogique',
          kind: 'info',
          send_email: true,
          pdf_base64: pdfBase64,
          pdf_filename: filename,
        }),
      });
      if (r.ok) sent += 1; else failed += 1;
    } catch (_e) {
      failed += 1;
    }
  }

  // Supervisor's combined PDF copy — always produced regardless of email status.
  generateCohortProfilePDF({
    entries,
    cohortName: master.holder_name || master.establishment || 'cohorte',
  });

  alertFn(
    `Export terminé.\n`
    + `• ${sent} apprenant·s notifié·s (in-game + email).\n`
    + (failed > 0 ? `• ${failed} échec·s d'envoi.\n` : '')
    + `• Votre copie consolidée a été téléchargée.`,
  );

  return { sent, failed, skipped };
}
